import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MasterFlightRecord } from '../../../../../../utils/types';
import { useLazyGetMasterFlightsQuery } from '../../../../../../services/redux/api';
import { FormSection } from '../../../../common';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { formatDisplayDate, getRouteString, formatFrequencyWithUnderscores } from '../../../../../../utils/types';

interface ParentFlightSelectorProps {
  draft: MasterFlightRecord;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
  onParentSelect: (parent: MasterFlightRecord) => void;
}

export function ParentFlightSelector({ draft, setDraft, onParentSelect }: ParentFlightSelectorProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [triggerSearch, { data: response, isLoading, isFetching }] = useLazyGetMasterFlightsQuery();
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const results = response?.data?.filter(f => !f.isAdhoc) || [];
  const selectedParentId = draft.parentFlightId;

  const doSearch = useCallback((searchTerm: string) => {
    if (searchTerm.trim().length < 2) return;
    triggerSearch({ search: searchTerm.trim(), limit: 20, page: 1 });
    setIsOpen(true);
  }, [triggerSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      setIsOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = (parent: MasterFlightRecord) => {
    setDraft(prev => ({
      ...prev,
      parentFlightId: parent._id,
      flightOwner: parent.flightOwner,
      flightNo: parent.flightNo,
      seasonId: parent.seasonId,
      iataServiceTypeCode: parent.iataServiceTypeCode,
      stations: parent.stations.map(s => ({ ...s })),
      startDate: parent.startDate,
      endDate: parent.endDate,
      frequency: parent.frequency,
      frequencyMYT: parent.frequencyMYT,
      isAdhoc: true,
    }));
    setQuery('');
    setIsOpen(false);
    onParentSelect(parent);
  };

  const handleClear = () => {
    setDraft(prev => ({
      ...prev,
      parentFlightId: undefined,
    }));
    setQuery('');
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  // If parent is already selected, show summary banner
  if (selectedParentId) {
    return (
      <FormSection title="Parent Flight" icon={<LinkIcon sx={{ fontSize: 20 }} />} collapsible defaultExpanded>
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="text-green-600" sx={{ fontSize: 20 }} />
            <div>
              <p className="text-sm font-semibold text-green-900">
                Parent: {draft.flightNo} ({draft.flightOwner}) — {draft.seasonId}
              </p>
              <p className="text-xs text-green-700">
                {formatDisplayDate(draft.startDate)} → {formatDisplayDate(draft.endDate)} · {getRouteString(draft.stations)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-1.5 text-sm font-medium text-green-700 hover:text-green-900 border border-green-300 rounded-md bg-white hover:bg-green-50"
          >
            Change
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Now customize the dates, frequency, and times for this adhoc override.
        </p>
      </FormSection>
    );
  }

  return (
    <FormSection title="Select Parent Flight" icon={<LinkIcon sx={{ fontSize: 20 }} />} collapsible defaultExpanded>
      <p className="text-sm text-gray-600 mb-3">
        Search for the regular schedule flight that this adhoc will override.
      </p>

      {/* Search input with dropdown */}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <SearchIcon sx={{ fontSize: 20 }} />
          </span>
          <input
            type="text"
            value={query}
            onChange={e => handleInputChange(e.target.value)}
            onFocus={() => { if (query.trim().length >= 2 && results.length > 0) setIsOpen(true); }}
            placeholder="Search by flight number, owner, or route (e.g. MH 0001, KUL)..."
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setIsOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>

        {/* Loading indicator */}
        {(isLoading || isFetching) && (
          <div className="absolute left-0 right-0 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-50 text-sm text-gray-500">
            Searching...
          </div>
        )}

        {/* Dropdown results */}
        {isOpen && !isLoading && !isFetching && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
            {results.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                No flights found matching "{query}"
              </div>
            ) : (
              <div className="py-1">
                {results.map((flight) => (
                  <button
                    key={flight._id}
                    type="button"
                    onClick={() => handleSelect(flight)}
                    className="w-full text-left px-4 py-2.5 hover:bg-primary-50 transition-colors cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">
                          {flight.flightOwner} {flight.flightNo}
                        </span>
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                          {flight.seasonId}
                        </span>
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium">
                          v{flight.itineraryVarId}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        {getRouteString(flight.stations)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500">
                        {formatDisplayDate(flight.startDate)} → {formatDisplayDate(flight.endDate)}
                      </span>
                      <span className="font-mono text-xs text-gray-400">
                        {formatFrequencyWithUnderscores(flight.frequency)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </FormSection>
  );
}
